<script setup lang="ts">
import { APP_NAME, getPageTitle } from "@/assets/js/appConfig";
import { useDefaultStore } from "@/store";

;

const store = useDefaultStore();

type TMusicRouteName = "MusicClassicGamePage" | "MusicBeatTheClockPage" | null;

type TMusicGameMode = {
	title: string;
	description: string;
	icon: string;
	routeName: TMusicRouteName;
	disabled: boolean;
	isMultiplayer?: boolean;
	scores?: {
		best: number;
		last: number;
	};
};

const classicBestScore = computed(
	() => store.userData?.usr_music_classic_best_score ?? 0,
);
const classicLastScore = computed(
	() => store.userData?.usr_music_classic_last_score ?? 0,
);
const btcBestScore = computed(
	() => store.userData?.usr_music_btc_best_score ?? 0,
);
const btcLastScore = computed(
	() => store.userData?.usr_music_btc_last_score ?? 0,
);

const gameModes = computed<TMusicGameMode[]>(() => [
	{
		title: "Classic",
		description: "Play a standard round and see how far you can go.",
		icon: "🎵",
		routeName: "MusicClassicGamePage",
		disabled: false,
		scores: {
			best: classicBestScore.value,
			last: classicLastScore.value,
		},
	},
	{
		title: "Beat the Clock",
		description: "Answer quickly before time runs out.",
		icon: "⏱️",
		routeName: "MusicBeatTheClockPage",
		disabled: false,
		scores: {
			best: btcBestScore.value,
			last: btcLastScore.value,
		},
	},
	{
		title: "Play with Others",
		description: "Challenge friends and compete together.",
		icon: "👥",
		routeName: null,
		disabled: false,
		isMultiplayer: true,
	},
]);

useHead({
	title: getPageTitle("Music"),
	meta: [
		{
			name: "title",
			content: getPageTitle("Music"),
		},
		{
			name: "description",
			content:
				"Challenge your music knowledge with quick comparison games based on release years.",
		},
		{ name: "robots", content: "index, follow" },
		{
			property: "og:title",
			content: getPageTitle("Music"),
		},
		{
			property: "og:description",
			content:
				"Challenge your music knowledge with quick comparison games based on release years.",
		},
		{ property: "og:site_name", content: APP_NAME },
		{
			name: "viewport",
			content: "width=device-width, initial-scale=1, viewport-fit=cover",
		},
	],
});
</script>

<template>
	<CategoryPageComp
		:category-code="store.MAIN_CATEGORIES.MUSIC"
		category-name="Music"
		fallback-route-name="MusicPage"
		:game-modes="gameModes"
		route-target-settings="MusicSettingsPage"
		route-target-leaderboard="MusicLeaderboard"
	/>
</template>
